// Devices and phone numbers tracked as assets (mig 0015).
//
// Current Owner is a tester or FRT (internal): exactly one of
// owner_tester_id / owner_frt is set. A phone number can be assigned to one
// device at a time (device_id), or to none.

import type { ISO8601 } from './primitives.js';

export type PhoneLineType = 'mobile' | 'landline' | 'voip';

export const PHONE_LINE_TYPES: { value: PhoneLineType; label: string }[] = [
  { value: 'mobile', label: 'Mobile' },
  { value: 'landline', label: 'Landline' },
  { value: 'voip', label: 'VoIP' },
];

export const DEVICE_IDENTIFIER_MAX = 64;
export const DEVICE_TEXT_MAX = 100;

/** Configurable option lists: device ownership, phone provider, payment plan. */
export interface AssetOptionRow {
  id: number;
  name: string;
  active: boolean;
  sort_order: number;
}

/** A customer profile that uses the asset (its phone or an OOB entry). */
export interface AssetLinkedProfile {
  customer_profile_id: number;
  /** "ALLY · Personal · JerryT" */
  display: string;
  /** e.g. "Profile phone", "OOB: Phone SMS (via +1 5551234567)" */
  usage: string;
  account_count: number;
}

export interface DeviceRow {
  id: number;
  device_identifier: string;
  /** Manufacturer / model lookups (mig 0020); make/model carry their names. */
  manufacturer_id: number | null;
  make: string | null;
  model_id: number | null;
  model: string | null;
  os_id: number | null;
  os_name: string | null;
  sim_restriction_id: number | null;
  sim_restriction_name: string | null;
  /** Jailbroken (iOS) / Rooted (Android) */
  root_status_id: number | null;
  root_status_name: string | null;
  location_city: string | null;
  location_region: string | null;
  ownership_id: number | null;
  ownership_name: string | null;
  /** Device Custodian: a tester or FRT (internal). */
  owner_tester_id: number | null;
  owner_frt: boolean;
  /** Tester short name, 'FRT (internal)', or null when not yet set. */
  owner_display: string | null;
  notes: string | null;
  active: boolean;
  created_at: ISO8601;
  updated_at: ISO8601;
  /** Phone numbers currently assigned to this device. */
  assigned_phones: { id: number; display: string }[];
  linked_profile_count: number;
}

export interface DeviceDetail extends DeviceRow {
  linked_profiles: AssetLinkedProfile[];
}

export interface DeviceUpsert {
  device_identifier: string;
  manufacturer_id: number;
  model_id: number;
  os_id?: number | null;
  sim_restriction_id?: number | null;
  root_status_id?: number | null;
  location_city?: string | null;
  location_region?: string | null;
  ownership_id: number;
  owner_tester_id?: number | null;
  owner_frt?: boolean;
  notes?: string | null;
  active?: boolean;
  /** Full set of phone numbers on this device. Numbers on another device
   *  move here; numbers left out are unassigned (no device). Omit to leave
   *  assignments unchanged. */
  phone_ids?: number[];
}

export interface PhoneNumberRow {
  id: number;
  country_code: string | null;
  number: string;
  /** "+1 5551234567" */
  display: string;
  line_type: PhoneLineType | null;
  /** Carrier (labelled "Carrier" since mig 0020; was "Provider"). */
  provider_id: number | null;
  provider_name: string | null;
  /** Per phone, as one carrier sells plans of different tiers (mig 0020). */
  carrier_type_id: number | null;
  carrier_type_name: string | null;
  payment_plan_id: number | null;
  payment_plan_name: string | null;
  payment_responsibility_id: number | null;
  payment_responsibility_name: string | null;
  test_status_id: number | null;
  test_status_name: string | null;
  usage_status_id: number | null;
  usage_status_name: string | null;
  activation_date: string | null;
  /** Subscriber: a tester or FRT (internal). */
  owner_tester_id: number | null;
  owner_frt: boolean;
  owner_display: string | null;
  /** Porting history summary. */
  port_count: number;
  last_port_date: string | null;
  device_id: number | null;
  device_identifier: string | null;
  /** "Apple iPhone 15 (FRT-PH-012)" */
  device_display: string | null;
  notes: string | null;
  active: boolean;
  created_at: ISO8601;
  updated_at: ISO8601;
  linked_profile_count: number;
}

export interface PhoneNumberDetail extends PhoneNumberRow {
  linked_profiles: AssetLinkedProfile[];
  /** Newest first. */
  ports: PhonePortEvent[];
}

/** One port of a number between carriers (mig 0020). */
export interface PhonePortEvent {
  id: number;
  phone_number_id: number;
  port_date: string;
  from_carrier_id: number | null;
  from_carrier_name: string | null;
  to_carrier_id: number;
  to_carrier_name: string;
  note: string | null;
  created_by_username: string | null;
  created_at: ISO8601;
}

export interface PhonePortCreate {
  port_date: string;
  /** Defaults to the phone's current carrier. */
  from_carrier_id?: number | null;
  to_carrier_id: number;
  note?: string | null;
}

export interface PhoneNumberUpsert {
  country_code: string;
  number: string;
  line_type: PhoneLineType;
  provider_id: number;
  carrier_type_id: number;
  payment_plan_id: number;
  payment_responsibility_id: number;
  test_status_id: number;
  usage_status_id: number;
  activation_date?: string | null;
  owner_tester_id?: number | null;
  owner_frt?: boolean;
  /** Device the number is assigned to; null = no device. */
  device_id?: number | null;
  notes?: string | null;
  active?: boolean;
}

export interface PhoneAssignDeviceRequest {
  device_id: number | null;
}

export interface AssetListQuery {
  q?: string;
  owner_tester_id?: number;
  /** 'true' (default) = active only, 'false' = retired only, 'all' */
  active?: 'true' | 'false' | 'all';
}

/** One change-history line: who changed what, when. */
export interface AssetHistoryEntry {
  id: number;
  at: ISO8601;
  action: string;
  /** null for system changes (e.g. the 0015 data conversion). */
  actor_username: string | null;
  changes: { field: string; before: string | null; after: string | null }[];
  note: string | null;
}
